'use client';

import { c as _c } from "react/compiler-runtime";
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useRef } from 'react';
import { ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS } from './shared';
import { useActivitySearchMode } from './useActivitySearchMode';
import { setPendingPrompt } from './activity-ai-chat-runtime-store';
import { useActivityAiActiveChat } from './useActivityAiActiveChat';

/**
 * Bridges a prompt carried over from another page (homepage AI bar, global
 * search modal) into the AI chat on mount. Two params, two intents:
 *
 * - `?startPrompt=` → run it immediately. Switch to AI mode, reveal the results
 *   sidebar and open a fresh chat with that prompt — the homepage "press search"
 *   flow lands here already searching, no second click. Mirrors the hero/mobile
 *   composer submit (reveal + sendMessage/startNewChat).
 * - `?prompt=` → only prefill the chat panel's draft textarea (the user reviews,
 *   then submits).
 *
 * Both strip their param from the URL once consumed.
 */
export function ActivityAiAutoPrompt() {
  const $ = _c(16);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parseAsString.withOptions(ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [prompt, setPrompt] = useQueryState("prompt", t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = parseAsString.withOptions(ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS);
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [startPrompt, setStartPrompt] = useQueryState("startPrompt", t1);
  const {
    isAi,
    setMode
  } = useActivitySearchMode();
  const {
    chatId,
    sendMessage,
    startNewChat
  } = useActivityAiActiveChat();
  const promptFiredRef = useRef(null);
  const startPromptFiredRef = useRef(null);
  let t2;
  let t3;
  if ($[2] !== isAi || $[3] !== prompt || $[4] !== setPrompt) {
    t2 = () => {
      const trimmed = prompt?.trim();
      if (!isAi || !trimmed) {
        return;
      }
      if (promptFiredRef.current === trimmed) {
        return;
      }
      promptFiredRef.current = trimmed;
      setPendingPrompt(trimmed);
      setPrompt(null);
    };
    t3 = [isAi, prompt, setPrompt];
    $[2] = isAi;
    $[3] = prompt;
    $[4] = setPrompt;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[7] !== chatId || $[8] !== isAi || $[9] !== sendMessage || $[10] !== setMode || $[11] !== setStartPrompt || $[12] !== startNewChat || $[13] !== startPrompt) {
    t4 = () => {
      const seed = startPrompt?.trim();
      if (!seed) {
        return;
      }
      if (startPromptFiredRef.current === seed) {
        return;
      }
      startPromptFiredRef.current = seed;
      if (!isAi) {
        setMode("ai");
      }
      if (chatId.length > 0) {
        startNewChat(seed);
      } else {
        sendMessage(seed);
      }
      setStartPrompt(null);
    };
    t5 = [isAi, setMode, startPrompt, setStartPrompt, chatId, sendMessage, startNewChat];
    $[7] = chatId;
    $[8] = isAi;
    $[9] = sendMessage;
    $[10] = setMode;
    $[11] = setStartPrompt;
    $[12] = startNewChat;
    $[13] = startPrompt;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  useEffect(t4, t5);
  return null;
}